/**
 * Teen Patti 20-20 — the TABLE CLOCK seam. Same shape as the Aviator / Andar
 * Bahar clocks. Round: betting 15s → locked (one of 96 CSPRNG deals is
 * picked) → revealing (six cards flip alternately A,B,A,B,A,B at the cadence)
 * → result → 3s intermission.
 */
import { type Card } from '../_cards/deck';
import { type Side, type Deal, type HandRank, candidates, betReturn, PAYOUT } from './engine';
import { mulberry32, mix } from '../aviator/prng';

export const WINDOW_MS = 15_000;
export const ARM_MS = 800;
export const FLIP_MS = 1_100;
export const RESULT_MS = 1_500;
export const INTERMISSION_MS = 3_000;

export type RoundStatus = 'betting' | 'locked' | 'revealing' | 'result' | 'intermission';

export type Round = {
  no: number;
  status: RoundStatus;
  opensAt: number;
  locksAt: number;
  revealStartsAt: number;
  /** cards revealed so far, per side (0..3 each) */
  shownA: Card[];
  shownB: Card[];
  /** known once status ≥ 'result' */
  winner: Side | 'push' | null;
  rankA: HandRank | null;
  rankB: HandRank | null;
  settlesAt: number | null;
  nextOpensAt: number | null;
  fakeSeed: number;
};

export type Bet = { side: Side; amount: number; id: string };
export interface Wallet { balance(): number; debit(n: number): boolean; credit(n: number): void; }

export interface TableClock {
  serverNow(): number;
  round(): Round;
  myBets(): Bet[];
  history(): { winner: Side | 'push'; catA: number; catB: number }[];
  subscribe(cb: () => void): () => void;
  bet(side: Side, amount: number): { ok: true } | { ok: false; reason: 'locked' | 'balance' };
  unbet(): number;
  myOutcome(): { staked: number; returned: number; net: number } | null;
  /**
   * Is the balance on screen the REAL one? (2026-09-06)
   *
   * The practice clock funds itself, so it is true from the first frame. The server clock only
   * learns the player's balance when its first `/state` lands, and until then the Page would
   * otherwise show the seeded practice figure — a number the player does not have. On a
   * real-money table that is a lie, and worse, `bet()` gates affordability on it, so a player
   * could compose a stake the server then refuses for reasons the UI never showed. The Page
   * shows a placeholder and holds betting closed until this goes true.
   */
  walletReady(): boolean;
  destroy(): void;
}

export class LocalTableClock implements TableClock {
  private r: Round;
  private bets: Bet[] = [];
  private hist: { winner: Side | 'push'; catA: number; catB: number }[] = [];
  private subs = new Set<() => void>();
  private timer = 0;
  private rng: () => number;
  private plan: Deal | null = null;     // the decided deal (secret until revealed)
  private nextFlipAt = 0;
  private settled = false;
  private outcome: { staked: number; returned: number; net: number } | null = null;

  constructor(private tableSeed: number, private wallet: Wallet, firstRoundNo = 1) {
    this.rng = mulberry32(mix(tableSeed, 0x7E));
    for (let i = 0; i < 12; i++) { const d = candidates(this.rng, 1)[0]; this.hist.push({ winner: d.winner, catA: d.ra.cat, catB: d.rb.cat }); }
    this.r = this.open(firstRoundNo, performance.now() + 400);
    this.timer = window.setInterval(() => this.tick(), 100);
  }

  private open(no: number, opensAt: number): Round {
    return {
      no, status: 'betting', opensAt, locksAt: opensAt + WINDOW_MS, revealStartsAt: opensAt + WINDOW_MS + ARM_MS,
      shownA: [], shownB: [], winner: null, rankA: null, rankB: null, settlesAt: null, nextOpensAt: null,
      fakeSeed: mix(this.tableSeed, no),
    };
  }

  serverNow() { return performance.now(); }
  round() { return this.r; }
  myBets() { return this.bets; }
  history() { return this.hist; }
  myOutcome() { return this.outcome; }
  /** Practice chips are funded locally, so the figure on screen is never a guess. */
  walletReady() { return true; }
  subscribe(cb: () => void) { this.subs.add(cb); return () => { this.subs.delete(cb); }; }
  private emit() { this.subs.forEach((cb) => cb()); }

  private tick() {
    const now = this.serverNow(); const r = this.r; let changed = false;
    if (r.status === 'betting' && now >= r.locksAt) {
      r.status = 'locked';
      /* PHASE-2 SEAM: the server's scorer picks one of 96 deals; practice = uniform */
      const cs = candidates(this.rng);
      this.plan = cs[Math.floor(this.rng() * cs.length)];
      changed = true;
    }
    if (r.status === 'locked' && now >= r.revealStartsAt) { r.status = 'revealing'; this.nextFlipAt = now; changed = true; }
    if (r.status === 'revealing' && this.plan && now >= this.nextFlipAt) {
      const shown = r.shownA.length + r.shownB.length;
      if (shown < 6) {
        if (shown % 2 === 0) r.shownA = [...r.shownA, this.plan.a[r.shownA.length]];
        else r.shownB = [...r.shownB, this.plan.b[r.shownB.length]];
        this.nextFlipAt = now + FLIP_MS;
        changed = true;
      }
      if (r.shownA.length === 3 && r.shownB.length === 3) {
        r.status = 'result'; r.winner = this.plan.winner; r.rankA = this.plan.ra; r.rankB = this.plan.rb;
        r.settlesAt = now + RESULT_MS; r.nextOpensAt = now + RESULT_MS + INTERMISSION_MS;
        this.settle();
        this.hist = [{ winner: this.plan.winner, catA: this.plan.ra.cat, catB: this.plan.rb.cat }, ...this.hist].slice(0, 20);
        changed = true;
      }
    }
    if (r.status === 'result' && r.settlesAt != null && now >= r.settlesAt) { r.status = 'intermission'; changed = true; }
    if (r.status === 'intermission' && r.nextOpensAt != null && now >= r.nextOpensAt) {
      this.r = this.open(r.no + 1, now); this.bets = []; this.settled = false; this.outcome = null; this.plan = null; changed = true;
    }
    if (changed) this.emit();
  }

  private settle() {
    if (this.settled || !this.r.winner) return; this.settled = true;
    let staked = 0, returned = 0;
    for (const b of this.bets) { staked += b.amount; returned += betReturn(b.amount, b.side, this.r.winner); }
    if (returned > 0) this.wallet.credit(returned);         // push = win of exactly stake
    this.outcome = { staked, returned, net: returned - staked };
  }

  bet(side: Side, amount: number) {
    if (this.r.status !== 'betting') return { ok: false as const, reason: 'locked' as const };
    if (!this.wallet.debit(amount)) return { ok: false as const, reason: 'balance' as const };
    this.bets = [...this.bets, { side, amount, id: `${this.r.no}-${this.bets.length}` }];   // PHASE-2 SEAM
    this.emit();
    return { ok: true as const };
  }
  unbet() {
    if (this.r.status !== 'betting' || this.bets.length === 0) return 0;
    const total = this.bets.reduce((s, b) => s + b.amount, 0);
    this.wallet.credit(total); this.bets = []; this.emit();
    return total;
  }
  destroy() { window.clearInterval(this.timer); this.subs.clear(); }
}
export { PAYOUT };
